const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

export const fetchDryingCycles = async ({ limit, page, query }) => {
  try {
    const response = await fetch(`${API_URL}/drying-cycles/filter`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ limit, page, query }),
    });
    if (!response.ok) throw new Error('Error al obtener los ciclos de secado');
    return await response.json();
  } catch (error) {
    console.error('Error al obtener los ciclos de secado:', error);
    throw error;
  }
};

/**
 * Lista SOLO los ciclos de secado abortados.
 *
 * Endpoint dedicado (`POST /drying-cycles/aborted/filter`) que
 * internamente fuerza `abortado: true` en el query. Se usa desde la
 * tabla de ciclos abortados y desde el modal de registro, de modo
 * que su consumo sea independiente del listado general.
 *
 * El `query` que recibe NO debe incluir `abortado`: el backend lo
 * agrega por defecto.
 */
export const fetchAbortedDryingCycles = async ({ limit, page, query = {} } = {}) => {
  try {
    const response = await fetch(`${API_URL}/drying-cycles/aborted/filter`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ limit, page, query }),
    });
    if (!response.ok) throw new Error('Error al obtener los ciclos de secado abortados');
    return await response.json();
  } catch (error) {
    console.error('Error al obtener los ciclos de secado abortados:', error);
    throw error;
  }
};

export const getDryingCycleReportTotals = async ({ query = {} }) => {
  try {
    if (query?.createdAt) {
      query.fecha = query.createdAt;
      delete query.createdAt;
    }
    const response = await fetch(`${API_URL}/drying-cycles/reports/totals`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query }),
    });
    if (!response.ok) throw new Error('Error al obtener totales de ciclos de secado');
    return await response.json();
  } catch (error) {
    console.error('Error al obtener totales de ciclos de secado:', error);
    throw error;
  }
};

export const getDryingCycleDaily = async ({ query = {}, groupBy = 'day' }) => {
  try {
    const response = await fetch(`${API_URL}/drying-cycles/reports/daily`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query, groupBy }),
    });
    if (!response.ok) throw new Error('Error al obtener serie diaria de ciclos de secado');
    return await response.json();
  } catch (error) {
    console.error('Error al obtener serie diaria de ciclos de secado:', error);
    throw error;
  }
};

export const createDryingCycle = async (cycleData) => {
  try {
    const response = await fetch(`${API_URL}/drying-cycles/create`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(cycleData),
    });
    if (!response.ok) throw new Error('Error al crear el registro de ciclo de secado');
    return await response.json();
  } catch (error) {
    console.error('Error al crear el registro de ciclo de secado:', error);
    throw error;
  }
};

/**
 * Registra un ciclo de secado ABORTADO.
 *
 * A diferencia de `createDryingCycle`, este endpoint dedicado NO
 * descuenta un día a la fecha. Se usa desde el modal de ciclos
 * abortados, donde el operador registra el ciclo el mismo día en que
 * ocurre (no al día siguiente como en el flujo normal de cierre de
 * turno). Usar el endpoint equivocado aquí causaba que la fecha
 * quedara guardada con un día de menos.
 */
export const createAbortedDryingCycle = async (cycleData) => {
  try {
    const response = await fetch(`${API_URL}/drying-cycles/create-aborted`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(cycleData),
    });
    if (!response.ok) throw new Error('Error al crear el registro de ciclo de secado abortado');
    return await response.json();
  } catch (error) {
    console.error('Error al crear el registro de ciclo de secado abortado:', error);
    throw error;
  }
};

export const updateDryingCycle = async (cycleId, cycleData) => {
  try {
    const response = await fetch(`${API_URL}/drying-cycles/update`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: cycleId, data: cycleData }),
    });
    if (!response.ok) throw new Error('Error al actualizar el registro de ciclo de secado');
    return await response.json();
  } catch (error) {
    console.error('Error al actualizar el registro de ciclo de secado:', error);
    throw error;
  }
};

export const deleteDryingCycle = async (cycleId) => {
  try {
    const response = await fetch(`${API_URL}/drying-cycles/delete/${cycleId}`, {
      method: 'DELETE',
    });
    if (!response.ok) throw new Error('Error al eliminar el registro de ciclo de secado');
    return await response.json();
  } catch (error) {
    console.error('Error al eliminar el registro de ciclo de secado:', error);
    throw error;
  }
};

export const exportDryingCycles = async (query = {}) => {
  try {
    const qs = encodeURIComponent(JSON.stringify(query || {}));
    const response = await fetch(`${API_URL}/drying-cycles/export?query=${qs}`, {
      method: 'GET',
    });
    if (!response.ok) throw new Error('Error al exportar los ciclos de secado');
    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error al exportar los ciclos de secado:', error);
    throw error;
  }
};

export const getTotalDriedKilos = async (idLavanderia, dateRange) => {
  try {
    const response = await fetch(`${API_URL}/drying-cycles/total-kilos`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        idLavanderia,
        startDate: dateRange?.$gte,
        endDate: dateRange?.$lte,
      }),
    });
    if (!response.ok) throw new Error('Error al obtener los kilos secados');
    return await response.json();
  } catch (error) {
    console.error('Error al obtener los kilos secados:', error);
    throw error;
  }
};

export const getDryingParticipationByMachine = async ({ query = {}, limit = 10000, page = 1 } = {}) => {
  try {
    const resp = await fetch(`${API_URL}/drying-cycles/reports/participation-by-machine`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query }),
    });
    if (resp.ok) {
      const payload = await resp.json();
      const data = Array.isArray(payload?.data) ? payload.data : [];
      const totals = payload?.extra || { totalKilos: 0, totalCiclos: 0 };
      return { data, totals };
    }
    throw new Error('Falling back to local aggregation');
  } catch (err) {
    try {
      const resp = await fetchDryingCycles({ limit, page, query });
      const items = Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp) ? resp : []);
      const map = new Map();
      let totalKilos = 0;
      let totalCiclos = 0;
      for (const it of items) {
        const id = it?.maquina?.id || it?.maquina?._id || 'sin_id';
        const nombre = it?.maquina?.nombre || 'Sin máquina';
        const kilos = Number(it?.kilosSecados || it?.kilos || 0);
        const ciclos = Number(it?.ciclos || 0);
        totalKilos += kilos;
        totalCiclos += ciclos;
        if (!map.has(id)) map.set(id, { id, nombre, kilosSecados: 0, ciclos: 0 });
        const cur = map.get(id);
        cur.kilosSecados += kilos;
        cur.ciclos += ciclos;
        map.set(id, cur);
      }
      const data = Array.from(map.values())
        .map(m => ({
          ...m,
          pctKilos: totalKilos > 0 ? (m.kilosSecados / totalKilos) * 100 : 0,
          pctCiclos: totalCiclos > 0 ? (m.ciclos / totalCiclos) * 100 : 0,
        }))
        .sort((a, b) => b.ciclos - a.ciclos);
      return { data, totals: { totalKilos, totalCiclos } };
    } catch (error) {
      console.error('Error al calcular participación por secadora:', error);
      return { data: [], totals: { totalKilos: 0, totalCiclos: 0 } };
    }
  }
};
